import { useTranslations } from '@vocab/react';
import {
  Textarea,
  Button,
  HiddenVisually,
  Stack,
  IconDelete,
  Columns,
  Column,
  Actions,
} from 'braid-design-system';
import { useState } from 'react';

import translations from './EditJobNotes.vocab';

const maxCharacters = 500;

type EventHandler = () => void;

export const byteCount = (text: string) =>
  encodeURI(text).split(/%(?:u[0-9A-F]{2})?[0-9A-F]{2}|./).length - 1;

export type Mode = 'add' | 'edit' | 'delete';
export interface Props {
  index: number;
  value?: string;
  error?: string;
  onSave: (value: string, mode: Mode) => void;
  onDelete: () => void;
  onCancel: EventHandler;
  pending?: boolean;
  getIsDirty: () => void;
}

export const EditJobNotes = ({
  index,
  value,
  error,
  onSave,
  onDelete,
  onCancel,
  pending,
  getIsDirty,
}: Props) => {
  const [notes, setNotes] = useState(value || '');
  const count = byteCount(notes);
  const tooLong = count > maxCharacters;

  const { t } = useTranslations(translations);
  const message = t('Only you can see these notes');

  let errorMessage = '';
  if (error && !tooLong) {
    errorMessage = error;
  } else if (tooLong) {
    errorMessage = t('too long characters', {
      maxCharacters: maxCharacters.toString(),
    });
  }

  return (
    <Stack space="medium">
      <Textarea
        id={`edit-notes-${index}`}
        tone={error || tooLong ? 'critical' : 'neutral'}
        message={errorMessage}
        label={t('Notes')}
        autoFocus={true}
        description={message}
        data={{
          'hj-masked': 'true',
          automation: 'job-note-field',
        }}
        value={notes}
        onChange={(e) => {
          getIsDirty();
          setNotes(e.currentTarget.value || '');
        }}
        characterLimit={maxCharacters}
      />
      <Columns space="small" collapseBelow="tablet" alignY="center">
        <Column>
          <Actions>
            <Button
              tone="formAccent"
              onClick={() => {
                const mode = value ? 'edit' : 'add';
                onSave(notes, mode);
              }}
              data={{ automation: 'job-note-save' }}
              loading={pending}
            >
              {`${pending ? t('Saving') : t('Save')} `}
              <HiddenVisually>{t('Notes')}</HiddenVisually>
            </Button>
            <Button
              tone="formAccent"
              variant="transparent"
              onClick={onCancel}
              data={{ automation: 'job-note-cancel' }}
            >
              {`${t('Cancel')} `}
              <HiddenVisually>{t('Notes')}</HiddenVisually>
            </Button>
          </Actions>
        </Column>
        <Column width="content">
          {value ? (
            <Button
              variant="transparent"
              tone="critical"
              icon={<IconDelete />}
              onClick={onDelete}
              data={{ automation: 'job-note-delete' }}
            >
              {`${t('Delete')} `}
              <HiddenVisually>{t('Notes')}</HiddenVisually>
            </Button>
          ) : null}
        </Column>
      </Columns>
    </Stack>
  );
};
